import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Wand2, Sparkles, Save, Check, ArrowRight, Trash2, ShoppingCart, ExternalLink, RotateCcw, Hammer, Tag } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

export default function Build() {
  const { user, usage, refreshUsage } = useAuth();
  const nav = useNavigate();
  const [want, setWant] = useState("");
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState(null);
  const [saved, setSaved] = useState([]);

  const designLocked = user?.paid && usage?.applies && !usage.features?.design?.available;

  const loadSaved = () => api.get("/builds").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const generate = async (random) => {
    setBusy(true);
    setCur(null);
    try {
      const { data } = await api.post("/build/generate", { want: random ? "" : want });
      setCur(data);
      refreshUsage();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); refreshUsage(); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!cur) return;
    try {
      await api.post(`/build/${cur.id}/save`);
      setCur({ ...cur, saved: true });
      toast.success("Saved to your builds");
      loadSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/build/${id}`); if (cur?.id === id) setCur(null); loadSaved(); toast.success("Removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const makePath = async () => {
    if (!cur) return;
    try {
      const { data } = await api.post("/sessions", {
        title: cur.title, source: "build", summary: cur.description,
        pathData: { thisWeek: (cur.steps || [])[0] || `Start building: ${cur.title}`, summary: cur.description },
      });
      toast.success("Path started");
      nav(`/session/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const makeListing = async () => {
    if (!cur) return;
    try {
      const { data } = await api.post(`/build/${cur.id}/listing`);
      toast.success("Product listing created");
      nav(`/listings/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="build-page">
        <div>
          <div className="label-mono flex items-center gap-2"><Hammer className="w-4 h-4" /> Design & Build It</div>
          <h1 className="font-head font-bold text-2xl sm:text-3xl mt-1">Dream it, see it, build it</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Describe anything to build or create — or let AI surprise you. Get an image, dimensions, and a full build plan.</p>
        </div>

        <div className="pm-card p-5 space-y-3" data-testid="build-input-card">
          <textarea className="pm-input" rows={3} placeholder="What do you want to build or create? (e.g. a floating bookshelf, a raised garden bed, a phone stand)"
            value={want} onChange={(e) => setWant(e.target.value)} data-testid="build-want-input" />
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => generate(false)} disabled={busy || !want.trim() || designLocked} className="pm-btn text-sm flex items-center gap-2" data-testid="build-design-btn">
              <Wand2 className="w-4 h-4" /> {busy ? "Designing…" : designLocked ? "Not enough coins" : `Design it${user?.paid && usage?.features?.design ? ` · ${usage.features.design.cost} coins` : ""}`}
            </button>
            <button onClick={() => generate(true)} disabled={busy || designLocked} className="pm-btn-ghost text-sm flex items-center gap-2" data-testid="build-surprise-btn">
              <Sparkles className="w-4 h-4" /> Surprise me
            </button>
          </div>
          {designLocked && (
            <p className="text-xs" style={{ color: "var(--warning)" }} data-testid="build-locked-note">
              A design costs {usage?.features?.design?.cost} coins. Buy 50 more at the top of the page or wait for your monthly refresh.
            </p>
          )}
          {busy && <p className="label-mono" data-testid="build-loading">Creating your image and build plan… this can take up to a minute.</p>}
        </div>

        {cur && (
          <div className="pm-card p-5 space-y-4" data-testid="build-result">
            <img src={`${BE}${cur.image.url}`} alt={cur.title} className="w-full max-h-96 object-contain rounded" data-testid="build-image" />
            <div>
              <h2 className="font-head font-bold text-xl">{cur.title}</h2>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{cur.description}</p>
            </div>

            {(cur.estBuildCost || cur.estSellPrice || cur.suppliesTotal) && (
              <div className="space-y-2" data-testid="build-economics">
                <div className="grid grid-cols-2 gap-3">
                  <div className="pm-card p-3" data-testid="build-cost">
                    <div className="label-mono">Cost to build</div>
                    <div className="font-head font-bold text-lg">{cur.estBuildCost || cur.suppliesTotal || "—"}</div>
                  </div>
                  <div className="pm-card p-3" data-testid="build-sell">
                    <div className="label-mono">Could sell for</div>
                    <div className="font-head font-bold text-lg" style={{ color: "var(--earn)" }}>{cur.estSellPrice || "—"}</div>
                  </div>
                </div>
                {cur.suppliesTotal && (
                  <div className="pm-card p-3 flex items-center justify-between" data-testid="build-supplies-total">
                    <span className="label-mono">Total supplies (materials to make one)</span>
                    <span className="font-head font-bold text-lg">{cur.suppliesTotal}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <button onClick={save} disabled={cur.saved} className="pm-btn text-sm flex items-center gap-2" data-testid="build-save-btn">
                {cur.saved ? <><Check className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save it</>}
              </button>
              <button onClick={makeListing} className="pm-btn text-sm flex items-center gap-2" data-testid="build-listing-btn">
                <Tag className="w-4 h-4" /> Make it a product listing
              </button>
              <button onClick={makePath} className="pm-btn text-sm flex items-center gap-2" data-testid="build-makepath-btn">
                Make it a path <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => generate(!!want.trim() ? false : true)} disabled={busy || designLocked} className="pm-btn-ghost text-sm flex items-center gap-2" data-testid="build-again-btn" title={designLocked ? "Not enough coins" : ""}>
                <RotateCcw className="w-4 h-4" /> Try again
              </button>
            </div>

            {cur.dimensions?.length > 0 && (
              <div data-testid="build-dimensions">
                <div className="label-mono mb-1">Dimensions</div>
                <div className="grid sm:grid-cols-2 gap-1 text-sm">
                  {cur.dimensions.map((d, i) => <div key={i}><b>{d.part}:</b> {d.size}</div>)}
                </div>
              </div>
            )}

            {cur.steps?.length > 0 && (
              <div data-testid="build-steps">
                <div className="label-mono mb-1">How to build it</div>
                <ol className="list-decimal pl-5 space-y-1 text-sm" style={{ color: "var(--text-2)" }}>
                  {cur.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}

            {cur.cutList?.length > 0 && (
              <div data-testid="build-cutlist">
                <div className="label-mono mb-1">Cut list (how to cut each piece)</div>
                <div className="space-y-1.5">
                  {cur.cutList.map((c, i) => (
                    <div key={i} className="text-sm border-b pb-1.5" style={{ borderColor: "var(--border-subtle)" }}>
                      <div><b>{c.piece}</b> — {c.dimensions} {c.qty ? `(x${c.qty})` : ""}</div>
                      {c.material && <div className="label-mono">{c.material}</div>}
                      {c.notes && <div style={{ color: "var(--text-2)" }}>{c.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cur.materials?.length > 0 && (
              <div data-testid="build-materials">
                <div className="label-mono mb-1">Materials</div>
                <div className="space-y-1.5">
                  {cur.materials.map((m, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap text-sm border-b pb-1.5" style={{ borderColor: "var(--border-subtle)" }}>
                      <span className="font-medium">{m.name}</span>
                      {m.qty && <span className="label-mono">{m.qty}</span>}
                      {m.estCost && <span className="label-mono">{m.estCost}</span>}
                      {m.buyUrl && <a href={m.buyUrl} target="_blank" rel="noreferrer" className="pm-btn-ghost text-xs flex items-center gap-1 ml-auto" data-testid={`build-material-buy-${i}`}><ShoppingCart className="w-3 h-3" /> Buy <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cur.tools?.length > 0 && (
              <div data-testid="build-tools">
                <div className="label-mono mb-1">Tools needed</div>
                <div className="space-y-1.5">
                  {cur.tools.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap text-sm border-b pb-1.5" style={{ borderColor: "var(--border-subtle)" }}>
                      <span className="font-medium">{t.name}</span>
                      {t.ownedAlready
                        ? <span className="badge" style={{ background: "var(--earn)" }}>you have this</span>
                        : <span className="badge" style={{ background: "var(--warning)" }}>need to buy</span>}
                      {t.estCost && <span className="label-mono">{t.estCost}</span>}
                      {!t.ownedAlready && t.buyUrl && <a href={t.buyUrl} target="_blank" rel="noreferrer" className="pm-btn-ghost text-xs flex items-center gap-1 ml-auto" data-testid={`build-tool-buy-${i}`}><ShoppingCart className="w-3 h-3" /> Buy <ExternalLink className="w-3 h-3" /></a>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {saved.length > 0 && (
          <div data-testid="build-saved">
            <div className="label-mono mb-2">Your saved builds</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {saved.map((b) => (
                <div key={b.id} className="pm-card p-0 overflow-hidden" data-testid={`saved-build-${b.id}`}>
                  <button onClick={() => { setCur(b); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="block w-full text-left">
                    <img src={`${BE}${b.image.url}`} alt={b.title} className="w-full h-32 object-cover" />
                    <div className="p-2 text-sm font-medium leading-tight">{b.title}</div>
                  </button>
                  <button onClick={() => del(b.id)} className="pm-btn-ghost text-xs flex items-center gap-1 m-2" data-testid={`saved-build-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
